import { Box, styled } from "@mui/material";
import { ReactNode, useEffect, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { ProcoreToolMode, selectActiveToolMode } from "slices/procoreUx";

import { PROCORE_BOTTOM_ENTRY, TOOLBAR_ANIMATION, TOOLBAR_COLORS, TOOLBAR_CONTAINER } from "../constants";
import { bottomToolbarActions, selectActiveTab, selectIsExpanded } from "./slice";

/** Wrapper that clips the extension - for animation effect */
const ExtensionClip = styled(Box, {
    shouldForwardProp: (prop) => prop !== "isVisible",
})<{ isVisible: boolean }>(({ isVisible }) => ({
    overflow: "hidden",
    marginLeft: "-12px",
    zIndex: 1,
    maxWidth: isVisible ? "100vw" : "0",
    transition: isVisible
        ? `max-width ${TOOLBAR_ANIMATION.DURATION_OPEN}ms ${TOOLBAR_ANIMATION.EASING_OPEN}`
        : `max-width ${TOOLBAR_ANIMATION.DURATION}ms ${TOOLBAR_ANIMATION.EASING}`,
}));

/** The actual extension content that slides in/out */
const ExtensionContent = styled(Box, {
    shouldForwardProp: (prop) => prop !== "isVisible",
})<{ isVisible: boolean }>(({ isVisible }) => ({
    display: "flex",
    alignItems: "center",
    height: `${PROCORE_BOTTOM_ENTRY.TOOLBAR_HEIGHT}px`,
    background: `linear-gradient(90deg, ${TOOLBAR_COLORS.LIGHT_GREY} 0%, ${TOOLBAR_COLORS.WHITE} 50%)`,
    borderRadius: `0 ${TOOLBAR_CONTAINER.RADIUS}px ${TOOLBAR_CONTAINER.RADIUS}px 0`,
    border: TOOLBAR_CONTAINER.BORDER,
    borderLeft: "none",
    boxShadow: TOOLBAR_CONTAINER.SHADOW,
    pointerEvents: isVisible ? "auto" : "none",
    transition: isVisible
        ? `transform ${TOOLBAR_ANIMATION.DURATION_OPEN}ms ${TOOLBAR_ANIMATION.EASING_OPEN}`
        : `transform ${TOOLBAR_ANIMATION.DURATION}ms ${TOOLBAR_ANIMATION.EASING}`,
    transform: isVisible ? "translateX(0)" : "translateX(-100%)",
    maxWidth: "calc(100vw - 160px)",
}));

/** Inner scroll container for the buttons - for phone layout */
const ScrollContainer = styled(Box)`
    display: flex;
    align-items: center;
    gap: ${PROCORE_BOTTOM_ENTRY.GAP}px;
    padding: ${TOOLBAR_CONTAINER.PADDING}px;
    padding-left: 20px;
    white-space: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    /* Hide scrollbar but keep functionality */
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
        display: none;
    }
`;

interface BottomToolbarExtensionProps {
    /** Content to show when General tab is active */
    generalContent?: ReactNode;
    /** Content to show when Tools tab is active */
    toolsContent?: ReactNode;
    /** Content to show when a tool is active (tool-specific controls) */
    toolControlsContent?: ReactNode;
}

export function BottomToolbarExtension({
    generalContent,
    toolsContent,
    toolControlsContent,
}: BottomToolbarExtensionProps) {
    const dispatch = useAppDispatch();
    const activeTab = useAppSelector(selectActiveTab);
    const isExpanded = useAppSelector(selectIsExpanded);
    const isToolActive = useAppSelector(selectActiveToolMode) !== ProcoreToolMode.Default;
    const previousTabRef = useRef(activeTab);
    const wasExpandedRef = useRef(isExpanded);
    // Track the tab to display - keeps old content during collapse animation when switching tabs
    const displayTabRef = useRef(activeTab);
    // Track if we're in a transition (switching tabs while expanded)
    const isTransitioningRef = useRef(false);

    // Detect tab change and collapsing state synchronously (before useEffect runs)
    const tabChanged = previousTabRef.current !== activeTab;
    const wasExpanded = wasExpandedRef.current;
    const isCollapsing = wasExpanded && !isExpanded;

    // If tab changed and we're collapsing, mark as transitioning immediately
    if (tabChanged && isCollapsing && !isToolActive && !isTransitioningRef.current) {
        isTransitioningRef.current = true;
    }

    // Handle tab switching animation
    useEffect(() => {
        const tabChanged = previousTabRef.current !== activeTab;
        const wasExpanded = wasExpandedRef.current;
        const justCollapsed = wasExpanded && !isExpanded;
        const justExpanded = !wasExpanded && isExpanded;

        if (tabChanged && justCollapsed && !isToolActive) {
            const updateTimer = setTimeout(() => {
                displayTabRef.current = activeTab;
                isTransitioningRef.current = false;
                dispatch(bottomToolbarActions.expand());
            }, TOOLBAR_ANIMATION.DURATION);

            return () => clearTimeout(updateTimer);
        }

        // When expanding normally (not in transition), update display tab to match active tab
        if (justExpanded && !isTransitioningRef.current) {
            displayTabRef.current = activeTab;
        }

        // When tab changes while collapsed (user closed one tab, then opens another), update immediately
        if (tabChanged && !isExpanded && !wasExpanded) {
            displayTabRef.current = activeTab;
        }

        previousTabRef.current = activeTab;
        wasExpandedRef.current = isExpanded;
    }, [activeTab, isExpanded, isToolActive, dispatch]);

    // Determine what content to show
    // When collapsing with tab change, keep showing old content (displayTabRef)
    // Otherwise, use activeTab
    const displayTab = (isCollapsing && tabChanged) || isTransitioningRef.current ? displayTabRef.current : activeTab;
    let content: ReactNode = null;

    if (isToolActive && toolControlsContent) {
        // Tool is active - show tool-specific controls
        content = toolControlsContent;
    } else if (displayTab === "general") {
        content = generalContent;
    } else if (displayTab === "tools") {
        content = toolsContent;
    }

    return (
        <ExtensionClip isVisible={isExpanded}>
            <ExtensionContent isVisible={isExpanded}>
                <ScrollContainer>{content}</ScrollContainer>
            </ExtensionContent>
        </ExtensionClip>
    );
}
